import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchSubscriptionTypesAsync,
  createStudentSubscriptionAsync,
  computeEndDateAsync,
  fetchSuggestedStartDateAsync,
} from '../store/slices/studentsSlice';
import type { Student, StudentSubscription, SubscriptionTypeItem } from '../store/slices/studentsSlice';
import { formatDateEuropean } from '../utils/dateUtils';
import axios from 'axios';
import SubscriptionPeriodPicker, { type SubscriptionBusyPeriod } from './SubscriptionPeriodPicker';
import AsyncSearchSelect from './AsyncSearchSelect';
import type { SelectOption } from './AsyncSearchSelect';
import Select from './Select';
import './Students.css';
import './GroupForm.css';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

interface StudentOption extends SelectOption<number> {
  student: Student;
}

function normYmd(s: string | null | undefined): string {
  if (!s) return '';
  return s.includes('T') ? s.split('T')[0] : s;
}

// Subscriptions are student-scoped, so every existing subscription of the student is a busy period.
function buildBusyPeriods(subs: StudentSubscription[]): SubscriptionBusyPeriod[] {
  return subs
    .map((s) => {
      const end = s.effectiveEndDate ?? s.endDate ?? s.estimatedEndDate;
      if (!s.startDate || !end) return null;
      return { start: normYmd(s.startDate), end: normYmd(end) };
    })
    .filter((p): p is SubscriptionBusyPeriod => p != null);
}

export interface SubscriptionFormProps {
  /** Preselect this student (option is fetched from the API) */
  prefillStudentId?: number;
  /** Hide the student selector — the student is fixed by the caller (e.g. journal cell) */
  lockStudent?: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

/**
 * The create-subscription form body, shared between the /subscriptions/create
 * page and modals (journal grid). All rules (busy periods, suggested start,
 * auto end-date estimate, covered-lessons preview) live here once.
 */
const SubscriptionForm: React.FC<SubscriptionFormProps> = ({
  prefillStudentId,
  lockStudent = false,
  onSuccess,
  onCancel,
}) => {
  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.auth?.token);
  const { subscriptionTypes } = useAppSelector((state) => state.students);

  const [form, setForm] = useState({
    studentId: prefillStudentId ? prefillStudentId.toString() : '',
    subscriptionTypeId: '',
    lessonsCount: '',
    totalAmount: '',
    startDate: '',
    paymentDate: '',
    endDate: '',
  });
  const [selectedStudentOption, setSelectedStudentOption] = useState<StudentOption | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [estimatedEndDate, setEstimatedEndDate] = useState<string | null>(null);
  const [coveredDates, setCoveredDates] = useState<string[]>([]);
  const [busyPeriods, setBusyPeriods] = useState<SubscriptionBusyPeriod[]>([]);
  /**
   * True only when the teacher has explicitly picked an end date that differs from the
   * auto-filled estimate. When false we don't send endDate to the backend so the schedule-
   * based estimate stays authoritative (and updates when vacations change).
   */
  const [isEndDateManual, setIsEndDateManual] = useState(false);

  // hasManualEnd drives whether the estimate compute loop runs
  const hasManualEnd = isEndDateManual && Boolean(form.endDate?.trim());

  // Apply the first subscription type's defaults to the form when no type is selected yet.
  const applyDefaultSubscriptionType = useCallback((types: SubscriptionTypeItem[]) => {
    if (types.length === 0) return;
    const first = types.find((t) => !t.isFreeze && !t.isReward) || types[0];
    const lessons = first.defaultLessons;
    const ppl = parseFloat(first.pricePerLesson);
    setForm(prev => {
      if (prev.subscriptionTypeId) return prev;
      return {
        ...prev,
        subscriptionTypeId: first.id.toString(),
        lessonsCount: lessons.toString(),
        totalAmount: (lessons * ppl).toFixed(2),
      };
    });
  }, []);

  // Load subscription types on mount, then pre-fill defaults for the first type when
  // none is selected yet. The write happens inside an async callback so it stays out of
  // the synchronous effect body while preserving the original behavior.
  useEffect(() => {
    if (subscriptionTypes.length === 0) {
      dispatch(fetchSubscriptionTypesAsync()).unwrap()
        .then((types) => applyDefaultSubscriptionType(types))
        .catch(() => {});
    } else {
      Promise.resolve().then(() => applyDefaultSubscriptionType(subscriptionTypes));
    }
  }, [dispatch, subscriptionTypes, applyDefaultSubscriptionType]);

  // Pre-fill the student option when the caller fixed the student.
  useEffect(() => {
    if (prefillStudentId && !selectedStudentOption && token) {
      axios
        .get(`${API_BASE_URL}/students/${prefillStudentId}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          const s = res.data.student as Student;
          setSelectedStudentOption({
            value: s.id,
            label: `${s.lastName} ${s.firstName}${s.surname ? ' ' + s.surname : ''}`,
            student: s,
          });
        })
        .catch(() => {});
    }
  }, [prefillStudentId, token, selectedStudentOption]);

  // Load student options from API — used by AsyncSearchSelect
  const loadStudentOptions = useCallback(async (search: string): Promise<StudentOption[]> => {
    if (!token) return [];
    const res = await axios.get(`${API_BASE_URL}/students`, {
      params: { search, limit: 50 },
      headers: { Authorization: `Bearer ${token}` },
    });
    return (res.data.students as Student[]).map((s) => ({
      value: s.id,
      label: `${s.lastName} ${s.firstName}${s.surname ? ' ' + s.surname : ''}`,
      student: s,
    }));
  }, [token]);

  // The student's current group (schedule source) — shown read-only for context.
  const currentGroup = useMemo(() => {
    const groups = selectedStudentOption?.student?.groups ?? [];
    return groups.length ? groups[0] : null;
  }, [selectedStudentOption]);

  // Existing subscriptions of the student (gray periods)
  useEffect(() => {
    let cancelled = false;
    if (!form.studentId || !token) {
      Promise.resolve().then(() => {
        if (!cancelled) setBusyPeriods([]);
      });
      return () => {
        cancelled = true;
      };
    }
    (async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(`${API_BASE_URL}/students/${form.studentId}/subscriptions`, { headers });
        const subs = res.data.subscriptions as StudentSubscription[];
        if (!cancelled) setBusyPeriods(buildBusyPeriods(subs));
      } catch {
        if (!cancelled) setBusyPeriods([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.studentId, token]);

  const computedPricePerLesson = useMemo(() => {
    const lessons = parseInt(form.lessonsCount, 10);
    const total = parseFloat(form.totalAmount);
    if (!lessons || lessons <= 0 || !total || isNaN(total)) return null;
    return (total / lessons).toFixed(2);
  }, [form.lessonsCount, form.totalAmount]);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  // Auto-fill lessons/total defaults when subscription type changes
  const typeOptions = useMemo(
    () => subscriptionTypes
      .filter((t) => !t.isFreeze && !t.isReward)
      .map((t) => ({ value: t.id, label: t.name })),
    [subscriptionTypes]
  );
  const selectedTypeOption = typeOptions.find(o => o.value.toString() === form.subscriptionTypeId) ?? null;

  const handleTypeChange = useCallback((option: SelectOption<number> | null) => {
    setForm(prev => {
      const updated = { ...prev, subscriptionTypeId: option ? option.value.toString() : '' };
      const subType = option ? subscriptionTypes.find(t => t.id === option.value) : null;
      if (subType) {
        const lessons = subType.defaultLessons;
        const ppl = parseFloat(subType.pricePerLesson);
        updated.lessonsCount = lessons.toString();
        updated.totalAmount = (lessons * ppl).toFixed(2);
      }
      return updated;
    });
    setFormErrors(prev => ({ ...prev, subscriptionTypeId: '' }));
  }, [subscriptionTypes]);

  const handleStudentChange = useCallback((option: StudentOption | null) => {
    setSelectedStudentOption(option);
    setForm(prev => ({ ...prev, studentId: option ? option.value.toString() : '', endDate: '' }));
    setIsEndDateManual(false);
    setEstimatedEndDate(null);
    setCoveredDates([]);
    setFormErrors(prev => ({ ...prev, studentId: '' }));
  }, []);

  const handlePeriodChange = useCallback((next: { startDate: string; endDate: string }) => {
    setForm(prev => {
      const endChanged = next.endDate !== prev.endDate;
      if (endChanged) {
        // Teacher explicitly picked or cleared the end date
        setIsEndDateManual(!!next.endDate && next.endDate !== estimatedEndDate);
      }
      return { ...prev, startDate: next.startDate, endDate: next.endDate };
    });
    setFormErrors(prev => ({ ...prev, startDate: '', endDate: '' }));
  }, [estimatedEndDate]);

  // Suggest start date when a student is selected (schedule comes from their current group)
  useEffect(() => {
    if (form.studentId) {
      dispatch(fetchSuggestedStartDateAsync({
        studentId: parseInt(form.studentId, 10),
      })).unwrap().then((payload) => {
        if (payload) {
          setForm((prev) => ({ ...prev, startDate: payload }));
        }
      }).catch(() => {});
    }
  }, [form.studentId, dispatch]);

  // Compute estimated end date + covered lesson dates (debounced) — skip when manual end is set
  useEffect(() => {
    const canCompute = !hasManualEnd
      && form.studentId && form.startDate && form.lessonsCount;
    if (!canCompute) {
      // Clear the estimate; deferred so the write stays out of the synchronous effect body.
      let cancelled = false;
      Promise.resolve().then(() => {
        if (!cancelled) {
          setEstimatedEndDate(null);
          setCoveredDates([]);
        }
      });
      return () => {
        cancelled = true;
      };
    }
    const timer = setTimeout(() => {
      dispatch(computeEndDateAsync({
        studentId: parseInt(form.studentId, 10),
        startDate: form.startDate,
        lessonsCount: parseInt(form.lessonsCount, 10),
      })).unwrap().then((payload) => {
        setEstimatedEndDate(payload.estimatedEndDate);
        setCoveredDates(payload.coveredDates);
      }).catch(() => {});
    }, 400);
    return () => clearTimeout(timer);
  }, [form.studentId, form.startDate, form.lessonsCount, hasManualEnd, dispatch]);

  // Auto-fill the end date field with the estimate when teacher hasn't manually set one.
  // Deferred into a microtask so the write stays out of the synchronous effect body.
  useEffect(() => {
    if (isEndDateManual) return;
    let cancelled = false;
    Promise.resolve().then(() => {
      if (!cancelled) setForm(prev => ({ ...prev, endDate: estimatedEndDate ?? '' }));
    });
    return () => {
      cancelled = true;
    };
  }, [estimatedEndDate, isEndDateManual]);

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!form.studentId) errors.studentId = 'Required';
    if (!form.subscriptionTypeId) errors.subscriptionTypeId = 'Required';
    if (!form.lessonsCount || parseInt(form.lessonsCount, 10) < 1) errors.lessonsCount = 'Min 1';
    if (!form.totalAmount || parseFloat(form.totalAmount) < 0) errors.totalAmount = 'Required';
    if (!form.startDate) errors.startDate = 'Required';
    if (!form.paymentDate) errors.paymentDate = 'Required';
    const manual = form.endDate?.trim();
    if (manual && form.startDate && manual < form.startDate) {
      errors.endDate = 'Must be on or after start date';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSubmitting(true);
    setError(null);
    try {
      // Only send endDate if teacher explicitly set a date different from the auto estimate
      const manualEnd = isEndDateManual ? form.endDate?.trim() : undefined;
      await dispatch(createStudentSubscriptionAsync({
        studentId: parseInt(form.studentId, 10),
        data: {
          subscriptionTypeId: parseInt(form.subscriptionTypeId, 10),
          lessonsCount: parseInt(form.lessonsCount, 10),
          totalAmount: parseFloat(form.totalAmount),
          startDate: form.startDate,
          paymentDate: form.paymentDate,
          ...(manualEnd ? { endDate: manualEnd } : {}),
        },
      })).unwrap();
      onSuccess();
    } catch (err: unknown) {
      setError(typeof err === 'string' ? err : 'Failed to create subscription');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="group-form">
      {error && (
        <div className="error-message" style={{ margin: '1rem 1rem 0' }}>
          {error}
          <button type="button" onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      <div className="form-section">
        <h2>Subscription Details</h2>

        {!lockStudent && (
          <div className="form-row-compact">
            <div className="form-group">
              <label>Student *</label>
              <AsyncSearchSelect<number, StudentOption>
                value={selectedStudentOption}
                onChange={handleStudentChange}
                loadOptions={loadStudentOptions}
                placeholder="Search by name..."
                isError={!!formErrors.studentId}
                noOptionsMessage="No students found"
              />
              {formErrors.studentId && <span className="error-text">{formErrors.studentId}</span>}
            </div>
            <div className="form-group">
              <label>Current Group</label>
              <input
                type="text"
                className="form-input"
                value={form.studentId ? (currentGroup ? (currentGroup.name || currentGroup.level.toUpperCase()) : 'No group') : ''}
                placeholder="Select student first"
                disabled
              />
            </div>
          </div>
        )}
        {lockStudent && selectedStudentOption && (
          <div className="form-row-compact">
            <div className="form-group">
              <label>Student</label>
              <input type="text" className="form-input" value={selectedStudentOption.label} disabled />
            </div>
          </div>
        )}

        <div className="form-row-compact">
          <div className="form-group">
            <label>Subscription Type *</label>
            <Select<number, SelectOption<number>>
              value={selectedTypeOption}
              onChange={handleTypeChange}
              options={typeOptions}
              placeholder="Select type"
              isError={!!formErrors.subscriptionTypeId}
              isClearable={false}
              noOptionsMessage="No types"
            />
            {formErrors.subscriptionTypeId && <span className="error-text">{formErrors.subscriptionTypeId}</span>}
          </div>
          <div className="form-group">
            <label>Payment Date *</label>
            <input
              type="date"
              name="paymentDate"
              value={form.paymentDate}
              onChange={handleFormChange}
              className={`form-input ${formErrors.paymentDate ? 'error' : ''}`}
            />
            {formErrors.paymentDate && <span className="error-text">{formErrors.paymentDate}</span>}
          </div>
        </div>

        {form.studentId && (
          <SubscriptionPeriodPicker
            startDate={form.startDate}
            endDate={form.endDate}
            onChange={handlePeriodChange}
            busyPeriods={busyPeriods}
            errors={{ startDate: formErrors.startDate, endDate: formErrors.endDate }}
          />
        )}

        <div className="form-row-compact">
          <div className="form-group">
            <label>Lessons *</label>
            <input
              type="number"
              name="lessonsCount"
              value={form.lessonsCount}
              onChange={handleFormChange}
              className={`form-input ${formErrors.lessonsCount ? 'error' : ''}`}
              min="1"
            />
            {formErrors.lessonsCount && <span className="error-text">{formErrors.lessonsCount}</span>}
          </div>
          <div className="form-group">
            <label>Total Amount (BYN) *</label>
            <input
              type="number"
              name="totalAmount"
              value={form.totalAmount}
              onChange={handleFormChange}
              className={`form-input ${formErrors.totalAmount ? 'error' : ''}`}
              min="0"
              step="0.01"
            />
            {formErrors.totalAmount && <span className="error-text">{formErrors.totalAmount}</span>}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '2rem', padding: '0.75rem 0', fontSize: '0.95rem', color: '#4a5568' }}>
          <span><strong>Price per Lesson:</strong> {computedPricePerLesson ?? '—'} BYN</span>
          <span>
            <strong>End date:</strong>{' '}
            {isEndDateManual
              ? `${form.endDate ? formatDateEuropean(form.endDate) : '—'} (manual)`
              : estimatedEndDate
                ? `${formatDateEuropean(estimatedEndDate)} (auto)`
                : '—'}
          </span>
        </div>
        {!hasManualEnd && coveredDates.length > 0 && (
          <div style={{ padding: '0.25rem 0 0.5rem' }}>
            <p style={{ fontSize: '0.85rem', color: '#4a5568', margin: '0 0 0.4rem' }}>
              <strong>Covers {coveredDates.length} lesson{coveredDates.length === 1 ? '' : 's'}:</strong>
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
              {coveredDates.map((d) => (
                <span
                  key={d}
                  style={{
                    fontSize: '0.78rem',
                    color: '#4a5568',
                    background: '#edf2f7',
                    border: '1px solid #e2e8f0',
                    borderRadius: 'var(--radius)',
                    padding: '0.15rem 0.45rem',
                  }}
                >
                  {formatDateEuropean(d)}
                </span>
              ))}
            </div>
          </div>
        )}
        {!isEndDateManual && (
          <p style={{ fontSize: '0.8rem', color: '#718096', margin: '0 0 0.25rem' }}>
            End date auto-fills from the student's current group schedule. Future vacations will shift it automatically. Pick a date in the calendar above to override.
          </p>
        )}
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancel
        </button>
        <button type="button" onClick={handleSubmit} className="btn-success" disabled={submitting}>
          {submitting ? 'Creating...' : 'Create Subscription'}
        </button>
      </div>
    </div>
  );
};

export default SubscriptionForm;
